import { colors } from '@kmns-internal/furikake/tokens'
import { Stack, Typography } from '@mui/material'
import { differenceInDays, format, parseISO } from 'date-fns'
import { useHistory } from 'react-router-dom'
import { DateFnsDatePicker } from 'src/components/datepicker/DateFnsDatePicker'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useQueryDashboardReportSummaries } from 'src/features/monitoringDashboardAlpha/api/useQueryDashboardReportSummaries'
import { OverallSubmissionRate } from 'src/features/monitoringDashboardAlpha/components/OverallSubmissionRate/OverallSubmissionRate'
import { PlaceFilterDropDown } from 'src/features/monitoringDashboardAlpha/components/PlaceFilterDropDown/PlaceFilterDropDown'
import { PlaceSummaryTable } from 'src/features/monitoringDashboardAlpha/components/PlaceSummaryTable/PlaceSummaryTable'
import { SummaryCards } from 'src/features/monitoringDashboardAlpha/components/SummaryCards/SummaryCards'
import { buildDetailPath } from 'src/features/monitoringDashboardAlpha/detail/buildDetailPath'
import {
  getDefaultDateRange,
  maxSelectableDays,
  useMonitoringFilters,
} from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import { ReportStatusGroupKey } from 'src/features/monitoringDashboardAlpha/types'

const { RangePicker } = DateFnsDatePicker

const emptySummary = {
  implemented: 0,
  implementedAfterDeadline: 0,
  recording: 0,
  recordingOverdue: 0,
  notImplementedYet: 0,
  duringPeriod: 0,
  notStarted: 0,
  total: 0,
}

/**
 * モニタリングダッシュボード（アルファ版 / ダッシュボード（新））
 *
 * 現場別の実施率モニタリングを検証するためのアルファ版画面。
 * feature flag `monitoringDashboardAlpha` で出し分けている。
 */
export const MonitoringDashboard = () => {
  const history = useHistory()
  const { filters, resetFilterOf, setFilterOf } = useMonitoringFilters()
  const summariesQuery = useQueryDashboardReportSummaries(filters)
  const { showErrorSnackbar } = useSnackbar()

  // サマリーカード選択 → L1 で絞り込んでいる現場＋ステータスグループで詳細（L2）へ
  const handleSelectStatus = (status: ReportStatusGroupKey | null) => {
    history.push(
      buildDetailPath({
        dateRange: filters.dateRange,
        placeNodeUuids: filters.placeNodeUuids,
        status,
      }),
    )
  }

  // 件数セル選択 → クリックした行の現場＋ステータスグループで詳細（L2）へ
  const handleSelectCell = (
    placeNodeUuid: string,
    status: ReportStatusGroupKey | null,
  ) => {
    history.push(
      buildDetailPath({
        dateRange: filters.dateRange,
        placeNodeUuids: [placeNodeUuid],
        status,
      }),
    )
  }

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    // 両方クリアされたら既定（未指定）に戻す
    if (!startDate && !endDate) {
      resetFilterOf.dateRange()
      return
    }
    // 片方だけ未確定の状態では適用しない（parseISO('') の NaN による誤リセットを防ぐ）
    if (!startDate || !endDate) {
      return
    }
    // 1ヶ月を超える期間が選択されたらエラーを出し、適用しない
    if (
      differenceInDays(parseISO(endDate), parseISO(startDate)) >=
      maxSelectableDays
    ) {
      showErrorSnackbar('期間は1ヶ月以内で選択してください')
      return
    }
    setFilterOf.dateRange(startDate, endDate)
  }

  const {
    summary = emptySummary,
    placeReportSummaries = [],
    count = 0,
  } = summariesQuery.data || {}

  const lastUpdatedText =
    summariesQuery.dataUpdatedAt > 0
      ? format(new Date(summariesQuery.dataUpdatedAt), 'yyyy年M月d日 HH:mm')
      : '—'

  return (
    <Stack sx={{ height: '100%' }}>
      <Header>
        <HeaderTitle title="ダッシュボード（新）" isBeta />
      </Header>

      <Stack
        spacing={2}
        sx={{ p: 3, bgcolor: colors.background.grayLight, flex: 1 }}
      >
        {/* フィルタ行 */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <RangePicker
            format="yyyy-MM-dd"
            placeholder={['開始日', '終了日']}
            disabledDate={current => current > new Date()}
            // 日付未指定でも既定期間（今日）を表示し、対象期間が分かるようにする
            value={(() => {
              const [from, through] = filters.dateRange ?? getDefaultDateRange()
              return [new Date(from), new Date(through)]
            })()}
            onChange={(_, [startDate, endDate]) =>
              handleDateRangeChange(startDate, endDate)
            }
          />
          <PlaceFilterDropDown
            value={filters.placeNodeUuids}
            onChange={setFilterOf.placeNodeUuids}
          />
          <Typography
            variant="body2"
            sx={{ ml: 'auto', color: colors.text.secondary }}
          >
            最終更新：{lastUpdatedText}
          </Typography>
        </Stack>

        {summariesQuery.isError ? (
          <ErrorMessage>エラーが発生しました</ErrorMessage>
        ) : (
          <>
            <OverallSubmissionRate summary={summary} />
            <SummaryCards
              summary={summary}
              onSelectStatus={handleSelectStatus}
            />
            <PlaceSummaryTable
              summaries={placeReportSummaries}
              count={count}
              offset={filters.offset}
              limit={filters.limit}
              isLoading={summariesQuery.isPending || summariesQuery.isFetching}
              onChangePage={setFilterOf.offset}
              onChangeLimit={setFilterOf.limit}
              onSelectCell={handleSelectCell}
            />
          </>
        )}
      </Stack>
    </Stack>
  )
}
